import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MedSummaryAssetFileView, MedSummaryAssetOverview, MedSummaryUpdateRead, UpdateStatusResponse } from './schemas'

export type MedSummaryAddFilesStaging = {
  // The matter the staging belongs to; staging resets when the page mounts for a different matter
  matterId: string | null
  stagedUserFileIds: string[]
  // Direct uploads not yet initialized with a server file id
  pendingUploadClientIds: string[]
  // Pending uploads that should auto-stage once initialized (removed on cancel)
  attachedPendingUploadClientIds: string[]
}

export type MedSummaryAssetState = {
  // null = matter confirmed to have no billing summary yet (empty state); missing key = not fetched
  assetByMatterId: { [matterId: string]: MedSummaryAssetOverview | null }
  assetLoadingByMatterId: { [matterId: string]: boolean }
  assetErrorByMatterId: { [matterId: string]: boolean }

  filesByAssetId: { [assetId: string]: MedSummaryAssetFileView[] }
  // Latest polled status of the asset's most recent update run (includes terminal statuses)
  updateStatusByAssetId: { [assetId: string]: UpdateStatusResponse }
  // Full update history for the asset (newest-first)
  updateHistoryByAssetId: { [assetId: string]: MedSummaryUpdateRead[] }
  // summary_version whose entries are currently loaded into the entries/references slices
  loadedEntriesVersionByAssetId: { [assetId: string]: number }
  // Entries/files refresh failed (entries table shows its own error from a null list)
  dataErrorByAssetId: { [assetId: string]: boolean }

  initializePendingByMatterId: { [matterId: string]: boolean }
  initializeErrorByMatterId: { [matterId: string]: string | null }
  mutationPendingByAssetId: { [assetId: string]: boolean }
  mutationErrorByAssetId: { [assetId: string]: string | null }

  addFilesStaging: MedSummaryAddFilesStaging
}

const initialAddFilesStaging: MedSummaryAddFilesStaging = {
  matterId: null,
  stagedUserFileIds: [],
  pendingUploadClientIds: [],
  attachedPendingUploadClientIds: [],
}

const initialState: MedSummaryAssetState = {
  assetByMatterId: {},
  assetLoadingByMatterId: {},
  assetErrorByMatterId: {},
  filesByAssetId: {},
  updateStatusByAssetId: {},
  updateHistoryByAssetId: {},
  loadedEntriesVersionByAssetId: {},
  dataErrorByAssetId: {},
  initializePendingByMatterId: {},
  initializeErrorByMatterId: {},
  mutationPendingByAssetId: {},
  mutationErrorByAssetId: {},
  addFilesStaging: initialAddFilesStaging,
}

export const medSummaryAssetSlice = createSlice({
  name: 'medSummaryAssetState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    setAssetForMatter: (state, action: PayloadAction<{ matterId: string; asset: MedSummaryAssetOverview | null }>) => {
      state.assetByMatterId[action.payload.matterId] = action.payload.asset
    },
    setAssetLoading: (state, action: PayloadAction<{ matterId: string; isLoading: boolean }>) => {
      state.assetLoadingByMatterId[action.payload.matterId] = action.payload.isLoading
    },
    setAssetError: (state, action: PayloadAction<{ matterId: string; hasError: boolean }>) => {
      state.assetErrorByMatterId[action.payload.matterId] = action.payload.hasError
    },

    setFilesForAsset: (state, action: PayloadAction<{ assetId: string; files: MedSummaryAssetFileView[] }>) => {
      state.filesByAssetId[action.payload.assetId] = action.payload.files
    },
    setUpdateStatus: (state, action: PayloadAction<{ assetId: string; status: UpdateStatusResponse }>) => {
      state.updateStatusByAssetId[action.payload.assetId] = action.payload.status
    },
    setUpdateHistory: (state, action: PayloadAction<{ assetId: string; updates: MedSummaryUpdateRead[] }>) => {
      state.updateHistoryByAssetId[action.payload.assetId] = action.payload.updates
    },
    setLoadedEntriesVersion: (state, action: PayloadAction<{ assetId: string; version: number }>) => {
      state.loadedEntriesVersionByAssetId[action.payload.assetId] = action.payload.version
    },
    setDataError: (state, action: PayloadAction<{ assetId: string; hasError: boolean }>) => {
      state.dataErrorByAssetId[action.payload.assetId] = action.payload.hasError
    },

    setInitializePending: (state, action: PayloadAction<{ matterId: string; isPending: boolean }>) => {
      state.initializePendingByMatterId[action.payload.matterId] = action.payload.isPending
    },
    setInitializeError: (state, action: PayloadAction<{ matterId: string; error: string | null }>) => {
      state.initializeErrorByMatterId[action.payload.matterId] = action.payload.error
    },
    setMutationPending: (state, action: PayloadAction<{ assetId: string; isPending: boolean }>) => {
      state.mutationPendingByAssetId[action.payload.assetId] = action.payload.isPending
    },
    setMutationError: (state, action: PayloadAction<{ assetId: string; error: string | null }>) => {
      state.mutationErrorByAssetId[action.payload.assetId] = action.payload.error
    },

    // Add Files staging
    resetAddFilesStaging: (state, action: PayloadAction<{ matterId: string }>) => {
      state.addFilesStaging = { ...initialAddFilesStaging, matterId: action.payload.matterId }
    },
    stageUserFileIds: (state, action: PayloadAction<{ fileIds: string[] }>) => {
      const existing = new Set(state.addFilesStaging.stagedUserFileIds)
      for (const fileId of action.payload.fileIds) {
        if (!existing.has(fileId)) {
          state.addFilesStaging.stagedUserFileIds.push(fileId)
          existing.add(fileId)
        }
      }
    },
    setStagedUserFileIds: (state, action: PayloadAction<{ fileIds: string[] }>) => {
      state.addFilesStaging.stagedUserFileIds = [...new Set(action.payload.fileIds)]
    },
    unstageUserFileId: (state, action: PayloadAction<{ fileId: string }>) => {
      state.addFilesStaging.stagedUserFileIds = state.addFilesStaging.stagedUserFileIds.filter((id) => id !== action.payload.fileId)
    },
    addPendingUserFileUpload: (state, action: PayloadAction<string>) => {
      if (!state.addFilesStaging.pendingUploadClientIds.includes(action.payload)) {
        state.addFilesStaging.pendingUploadClientIds.push(action.payload)
      }
      if (!state.addFilesStaging.attachedPendingUploadClientIds.includes(action.payload)) {
        state.addFilesStaging.attachedPendingUploadClientIds.push(action.payload)
      }
    },
    removePendingUserFileUpload: (state, action: PayloadAction<string>) => {
      state.addFilesStaging.pendingUploadClientIds = state.addFilesStaging.pendingUploadClientIds.filter((id) => id !== action.payload)
      state.addFilesStaging.attachedPendingUploadClientIds = state.addFilesStaging.attachedPendingUploadClientIds.filter((id) => id !== action.payload)
    },

    // Listener triggers (no state change; effects live in listeners.ts)
    fetchAssetForMatter: (_state, _action: PayloadAction<{ matterId: string; overviewOnly?: boolean }>) => {},
    refreshAssetData: (_state, _action: PayloadAction<{ assetId: string }>) => {},
    pollActiveUpdate: (_state, _action: PayloadAction<{ assetId: string; updateId: string }>) => {},
    fetchUpdateHistory: (_state, _action: PayloadAction<{ assetId: string }>) => {},
    initializeSummary: (_state, _action: PayloadAction<{ matterId: string; userFileIds: string[] }>) => {},
    applyUpdate: (_state, _action: PayloadAction<{ assetId: string; addUserFileIds: string[] }>) => {},
    retryFailedFiles: (_state, _action: PayloadAction<{ assetId: string; updateId: string }>) => {},
    completeExcludingFailed: (_state, _action: PayloadAction<{ assetId: string; updateId: string }>) => {},
  },
})

export const MedSummaryAssetActions = medSummaryAssetSlice.actions

export default medSummaryAssetSlice.reducer
